import React, { useState, useEffect } from 'react';
import { Filter, FileText, CheckCircle, Clock, XCircle, Calendar, Search, X, ArrowRight, Download, Eye, RotateCcw, AlertTriangle } from 'lucide-react';

import axios from "axios";
import qs from "qs";

import { API_BASE_URL } from "../api/config";
import API from "../api";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

export default function Dashboard() {
  const [userRole, setUserRole] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  // L2 officers filter by Block within their district (per client feedback).
  // L1 and everyone else still filter by District.
  const [selectedBlock, setSelectedBlock] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredApplications, setFilteredApplications] = useState([]);
  // Dashboard application list pagination
  const [appPage, setAppPage] = useState(1);
  const APP_PAGE_SIZE = 10;
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedTimeline, setSelectedTimeline] = useState([]);
  const [selectedAppContext, setSelectedAppContext] = useState(null);
  const [totalnocrequestlist, setTotalNocRequestList] = useState([]);
  const [pendingnocrequestlist, setPendingNocRequestList] = useState([]);
  const [approvednocrequestlist, setApprovedNocRequestList] = useState([]);
  const [rejectednocrequestlist, setRejectedNocRequestList] = useState([]);
  const [returnednocrequestlist, setReturnedNocRequestList] = useState([]);
  const [staffinfo, setStaffinfo] = useState([]);
  const [TimelineNOC, setTimelineNOC] = useState('');

  // (Edit / resubmit is now handled via the full tank activity UI at
  //  /edit-application/:id — no local modal state needed.)

  

     const navigate = useNavigate();
  
  // AuthContext is the sole source of the current user (pen-test remediation).
  const { user: authUser, role: authRole, isReady: authReady, isAuthenticated } = useAuth();

  useEffect(() => {
    if (authReady && !isAuthenticated) navigate('/login');
  }, [authReady, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authReady || !authUser) return;
    const userinfo = authUser;
    const storedRole = authRole || 'ngo';
    const storedEmail = authUser.email_id || '';

    setStaffinfo(userinfo);
    setUserRole(storedRole);
    setUserEmail(storedEmail);

    const userId = userinfo.user_id || userinfo.id;

    // Fetch applications + masters in parallel, then join client-side so the
    // existing UI gets readable names rather than raw FK ids.
    const safeGet = (url) => API.get(url).then((r) => (Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => []);

    Promise.all([
      safeGet('/applications'),
      safeGet('/tanks'),
      safeGet('/districts'),
      safeGet('/departments'),
    ]).then(async ([apps, tanks, districts, departments]) => {
      const tanksById = new Map(tanks.map((t) => [Number(t.tank_id), t]));
      const districtsById = new Map(districts.map((d) => [Number(d.district_id), d]));
      const departmentsById = new Map(departments.map((d) => [Number(d.department_id), d]));

      // NGOs only see their own applications.
      let scoped = apps;
      if (storedRole === 'ngo' && userId) {
        scoped = apps.filter((a) => Number(a.applied_by) === Number(userId));
      }
      // Department users: don't pre-filter by JWT department_id — the JWT
      // might be stale and the application's stored department_id might
      // point to a sibling row. Instead, fetch every application's chain
      // and keep the ones where THIS user appears anywhere in the chain.

      // Fetch each application's approver chain so we can render the level
      // progress strip and (for department users) decide whose turn it is.
      let chainsById = new Map();
      if (scoped.length > 0) {
        const chainResults = await Promise.all(
          scoped.map((a) =>
            API.get(`/applications/${a.application_id}/chain`)
              .then((r) => [a.application_id, Array.isArray(r?.data?.data) ? r.data.data : []])
              .catch(() => [a.application_id, []])
          )
        );
        chainsById = new Map(chainResults);
      }

      // /tanks is paginated (25/page). Applications reference tanks by ID
      // which may not be in the first page — fetch each referenced tank
      // individually so enrichment (sectionName / divisionName / etc.) is
      // populated. Without this, scope filters strip everything.
      const tankIds = [...new Set(scoped.map((a) => Number(a.tank_id)).filter(Boolean))];
      const missingTankIds = tankIds.filter((id) => !tanksById.has(id));
      if (missingTankIds.length) {
        const extra = await Promise.all(
          missingTankIds.map((id) =>
            API.get(`/tanks?tank_id=${id}`)
              .then((r) => (Array.isArray(r?.data?.data) ? r.data.data[0] : r?.data?.data) || null)
              .catch(() => null)
          )
        );
        extra.filter(Boolean).forEach((t) => tanksById.set(Number(t.tank_id), t));
      }

      const enriched = scoped.map((a) => {
        const tank = tanksById.get(Number(a.tank_id));
        const district = districtsById.get(Number(a.district_id));
        const department = departmentsById.get(Number(a.department_id));
        const districtName = district?.district_name || tank?.district_name || '';
        const blockName = tank?.block_name || '';

        const chain = chainsById.get(a.application_id) || [];
        const activeStep = chain.find((s) => String(s.status).toUpperCase() === 'PENDING') || null;
        const appStatusUpper = String(a.status || '').toUpperCase();
        const isTerminal = appStatusUpper === 'APPROVED' || appStatusUpper === 'REJECTED';
        const reasonStep = (appStatusUpper === 'REJECTED' || appStatusUpper === 'RETURNED')
          ? chain.filter((s) => String(s.status || '').toUpperCase() === appStatusUpper)
                 .sort((x, y) => new Date(y.action_date || 0) - new Date(x.action_date || 0))[0]
          : null;
        // Sent back to L1 by L2 (still PENDING, L1 active, L2 row keeps its remarks).
        const returnedToL1Step = (appStatusUpper === 'PENDING' && activeStep && Number(activeStep.level) === 1)
          ? chain.find((s) => Number(s.level) > 1 && String(s.comments || '').trim())
          : null;
        const isMyTurn =
          !isTerminal && !!activeStep && Number(activeStep.user_id) === Number(userId);

        return {
          ...a,
          id: `NOC${String(a.application_id).padStart(3, '0')}`,
          application_id: a.application_id,
          tankName: tank?.water_body_name || `Tank #${a.tank_id}`,
          location: [districtName, blockName].filter(Boolean).join(', '),
          districtName,
          blockName,
          sectionName: tank?.section_name && tank.section_name !== '-' ? tank.section_name : '',
          ulbName: tank?.ulb_name || '',
          // Extra tank fields used by WRD view-only scope narrowing.
          divisionName: tank?.division || '',
          circleName:   tank?.circle   || '',
          regionName:   tank?.region   || '',
          status: String(a.status || '').toLowerCase(),
          department: department?.department_name || tank?.owned_by || '',
          activities: a.activity_ids ? String(a.activity_ids).split(',') : [],
          date: a.created_at,
          stage: activeStep ? activeStep.level : a.stage || 0,
          activeLevel: activeStep ? activeStep.level : null,
          activeUserId: activeStep ? activeStep.user_id : null,
          chain,
          isMyTurn,
          latestReason: reasonStep ? (reasonStep.comments || '') : '',
          latestReasonLevel: reasonStep ? reasonStep.level : null,
          returnedToL1Reason: returnedToL1Step ? returnedToL1Step.comments : '',
          returnedToL1Level: returnedToL1Step ? returnedToL1Step.level : null,
          approvelevel: a.approvelevel || '0',
        };
      });

      // Department users see only applications routed to their department
      // (and district, when known). Falls back to "all" if the JWT didn't
      // carry department_id (older sessions). My-turn rows float first.
      //
      // WRD-specific: view-only role_types (L1 A / L2 A / Master) are further
      // narrowed to their office scope. Their JWT carries office_id;
      // we look up the office_name in officesById and match the appropriate
      // tank column per role.
      // Load every routing-target master so we can translate the JWT's
      // office_id / division_id / ulb_id / region_id / section_id / block_id
      // into a name, and compare that name to the tank record's fields.
      const loadMaster = async (path) => {
        try {
          const r = await API.get(path);
          return Array.isArray(r?.data?.data) ? r.data.data : [];
        } catch { return []; }
      };
      const [officesRaw, divisionsRaw, ulbsRaw, regionsRaw, sectionsRaw, blocksRaw] = await Promise.all([
        loadMaster('/offices'), loadMaster('/divisions'), loadMaster('/ulbs'),
        loadMaster('/regions'), loadMaster('/sections'), loadMaster('/blocks'),
      ]);
      const mapById = (arr, idKey, nameKey) =>
        new Map(arr.map((o) => [Number(o[idKey]), o[nameKey]]));
      const officeById   = mapById(officesRaw,   'office_id',   'office_name');
      const divisionById = mapById(divisionsRaw, 'division_id', 'division_name');
      const ulbById      = mapById(ulbsRaw,      'ulb_id',      'ulb_name');
      const regionById   = mapById(regionsRaw,   'region_id',   'region_name');
      const sectionById  = mapById(sectionsRaw,  'section_id',  'section_name');
      const blockById    = mapById(blocksRaw,    'block_id',    'block_name');

      // My scope names — resolved from whichever JWT id is populated.
      const myOfficeName   = userinfo.office_id   ? officeById.get(Number(userinfo.office_id))     : null;
      const myDivisionName = userinfo.division_id ? divisionById.get(Number(userinfo.division_id)) : null;
      const myUlbName      = userinfo.ulb_id      ? ulbById.get(Number(userinfo.ulb_id))           : null;
      const myRegionName   = userinfo.region_id   ? regionById.get(Number(userinfo.region_id))     : null;
      const mySectionName  = userinfo.section_id  ? sectionById.get(Number(userinfo.section_id))   : null;
      const myBlockName    = userinfo.block_id    ? blockById.get(Number(userinfo.block_id))       : null;

      // Officer-level scope narrowing: only see tanks whose relevant column
      // matches the officer's assigned scope. Different depts have different
      // mappings. WRD uses office_id (Section for L1, Division for L2A/Master
      // stored as office); RDPR uses block_id; GCC uses division_id (Zone) +
      // region_id (Region for L2); DMA uses ulb_id; HR&CE uses region_id.
      //
      // A `null` scope name means "no assignment yet" — we let it pass so the
      // officer at least sees data (safer than silently hiding everything).
      const matchAny = (candidates, target) => !target || candidates
        .map((c) => String(c || '').trim())
        .some((v) => v && v === String(target).trim());

      const scopeByRoleType = {
        // L1 approvers — office-based for WRD (Section), otherwise block/division/ulb/region per dept.
        'L1':     (r) => matchAny([r.sectionName, r.blockName, r.divisionName, r.ulbName, r.regionName],
                                  myOfficeName || myBlockName || myDivisionName || myUlbName || myRegionName || mySectionName),
        // L1 A (Sub-division view-only, WRD only) — office maps to Division.
        'L1 A':   (r) => matchAny([r.divisionName], myOfficeName),
        // L2 approvers — Division for WRD (via office), Region for GCC, ULB for DMA, District for RDPR (fallback via dept+district).
        'L2':     (r) => matchAny([r.divisionName, r.regionName, r.ulbName],
                                  myOfficeName || myRegionName || myDivisionName || myUlbName),
        // L2 A (Circle view-only, WRD only) — office maps to Circle.
        'L2 A':   (r) => matchAny([r.circleName], myOfficeName),
        // Master (Region view-only, WRD only) — office maps to Region.
        'Master': (r) => matchAny([r.regionName], myOfficeName),
      };

      // Client rule: office is the authoritative scope, district is not
      // considered. All department users are filtered by department + their
      // routing target (via scopeByRoleType below), not by the JWT district.
      let finalRows = enriched;
      if (storedRole === 'department' && userinfo.department_id) {
        // Per-tank-routed depts (Forest, HR&CE) carry no scope columns on the
        // officer; the only authoritative check is whether this user appears
        // in the application's chain (any level). Fall back to that whenever
        // every column-based scope value is null, which is how Forest/HR&CE
        // officers are seeded.
        const chainOnly = !myOfficeName && !myDivisionName && !myUlbName
          && !myRegionName && !mySectionName && !myBlockName;
        finalRows = enriched.filter((r) => {
          if (Number(r.department_id) !== Number(userinfo.department_id)) return false;
          if (chainOnly) {
            // Keep rows where this user appears anywhere in the approval chain.
            return (r.chain || []).some((s) => Number(s.user_id) === Number(userId));
          }
          // Apply per-role office scope narrowing.
          const scopeFn = scopeByRoleType[userinfo.role_type];
          if (scopeFn && !scopeFn(r)) return false;
          return true;
        });
        finalRows = [...finalRows.filter((r) => r.isMyTurn), ...finalRows.filter((r) => !r.isMyTurn)];
      } else if (storedRole === 'department') {
        finalRows = [...enriched.filter((r) => r.isMyTurn), ...enriched.filter((r) => !r.isMyTurn)];
      }

      setTotalNocRequestList(finalRows);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, authUser, authRole]);

  const handleSubmitDraft = async (applicationId) => {
    if (!window.confirm('Submit this draft application for approval? You will not be able to edit it after submission.')) return;
    try {
      const res = await API.post(`/applications/${applicationId}/submit`);
      if (res?.data?.success) {
        window.location.reload();
      } else {
        alert(res?.data?.message || 'Submit failed');
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Submit failed');
    }
  };

  // Edit / resubmit: open the full activity-selection UI pre-filled with the
  // application's current state. Route to /edit-application/:id.
  const openEdit = (app) => {
    window.location.href = `/edit-application/${app.application_id}`;
  };

  const handleDeleteDraft = async (applicationId) => {
    if (!window.confirm('Delete this draft application? This cannot be undone.')) return;
    try {
      const res = await API.delete(`/applications/${applicationId}`);
      if (res?.data?.success) {
        window.location.reload();
      } else {
        alert(res?.data?.message || 'Delete failed');
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Delete failed');
    }
  };

  const handleApprove = async (applicationId) => {
    const comments = window.prompt('Approval comments (optional):', '') || '';
    try {
      const res = await API.post(`/applications/${applicationId}/approve`, { comments });
      if (res?.data?.success) {
        window.location.reload();
      } else {
        alert(res?.data?.message || 'Approve failed');
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Approve failed');
    }
  };

  const handleReject = async (applicationId) => {
    const comments = window.prompt('Rejection reason (required):', '');
    if (!comments) return;
    try {
      const res = await API.post(`/applications/${applicationId}/reject`, { comments });
      if (res?.data?.success) {
        window.location.reload();
      } else {
        alert(res?.data?.message || 'Reject failed');
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Reject failed');
    }
  };

  const handleNewNOCRequest = () => {
    // Navigate to new NOC request form
    window.location.href = '/new-noc-request';
  };

  const handleWorkflowView = (applicationId) => {
    // Navigate to workflow page
    window.location.href = '/workflow/' + applicationId;
  };

  const handleDownload = (applicationId) => {
    // applicationId here may be "NOC003" or a number; resolve to numeric id.
    const local = (totalnocrequestlist || []).find(
      (a) => a.application_id === applicationId || a.id === applicationId
    );
    const numericId = local?.application_id || String(applicationId).replace(/^NOC/, '').replace(/^0+/, '');
    const url = `${API_BASE_URL}/applications/${numericId}/certificate`;
    window.open(url, '_blank');
  };

  // Sample application data with proper hierarchy timeline
  const applications = [
    {
      id: 'NOC001',
      tankName: 'Palar Tank',
      location: 'Vellore, Gudiyatham',
      activities: ['Strengthening of bund', 'Desilting of channel'],
      status: 'pending',
      department: 'WRD',
      timeline: [
        { date: '2025-08-18', status: 'pending_ee', level: 'EE', remarks: 'Remarks will come' },
        { date: '2025-08-16', status: 'approved_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-15', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-14', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC002',
      tankName: 'Kamakshi Tank',
      location: 'Chennai, Tambaram',
      activities: ['Formation of island', 'Plantation'],
      status: 'approved',
      department: 'RD',
      timeline: [
        { date: '2025-08-14', status: 'approved_dc', level: 'District Collector', remarks: 'Remarks will come' },
        { date: '2025-08-13', status: 'approved_ee', level: 'EE', remarks: 'Remarks will come' },
        { date: '2025-08-12', status: 'approved_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-11', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-10', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC003',
      tankName: 'Vaigai Tank',
      location: 'Madurai, Usilampatti',
      activities: ['Excavation of channels', 'Fencing'],
      status: 'rejected',
      department: 'Forest',
      timeline: [
        { date: '2025-08-12', status: 'rejected_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-08', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-05', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC004',
      tankName: 'Cooum Tank',
      location: 'Chennai, Egmore',
      activities: ['Walking track', 'Sitting benches'],
      status: 'pending',
      department: 'GCC',
      timeline: [
        { date: '2025-08-13', status: 'pending_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-12', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-12', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC005',
      tankName: 'Bhavani Tank',
      location: 'Erode, Bhavani',
      activities: ['Bathing ghats', 'Desilting of channel'],
      status: 'approved',
      department: 'HR & CE',
      timeline: [
        { date: '2025-08-03', status: 'approved_dc', level: 'District Collector', remarks: 'Remarks will come' },
        { date: '2025-08-02', status: 'approved_ee', level: 'EE', remarks: 'Remarks will come' },
        { date: '2025-08-02', status: 'approved_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-01', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-01', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC006',
      tankName: 'Palar Tank',
      location: 'Vellore, Gudiyatham',
      activities: ['Strengthening of bund', 'Desilting of channel'],
      status: 'pending',
      department: 'WRD',
      timeline: [
        { date: '2025-08-18', status: 'pending_ee', level: 'EE', remarks: 'Remarks will come' },
        { date: '2025-08-16', status: 'approved_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-15', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-14', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC007',
      tankName: 'Kamakshi Tank',
      location: 'Chennai, Tambaram',
      activities: ['Formation of island', 'Plantation'],
      status: 'approved',
      department: 'RD',
      timeline: [
        { date: '2025-08-14', status: 'approved_dc', level: 'District Collector', remarks: 'Remarks will come' },
        { date: '2025-08-13', status: 'approved_ee', level: 'EE', remarks: 'Remarks will come' },
        { date: '2025-08-12', status: 'approved_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-11', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-10', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    },
    {
      id: 'NOC008',
      tankName: 'Vaigai Tank',
      location: 'Madurai, Usilampatti',
      activities: ['Excavation of channels', 'Fencing'],
      status: 'pending',
      department: 'Forest',
      timeline: [
        { date: '2025-08-12', status: 'pending_aee', level: 'AEE', remarks: 'Remarks will come' },
        { date: '2025-08-08', status: 'approved_ae_je', level: 'AE/JE', remarks: 'Remarks will come' },
        { date: '2025-08-05', status: 'submitted', level: 'NGO', remarks: 'Remarks will come' }
      ]
    }
  ];

  // Filter applications based on search and filters
  useEffect(() => {

    // let filtered = applications;

    let filtered = totalnocrequestlist;

    const term = (searchTerm || '').trim().toLowerCase();
    const statusTerm = (selectedStatus || '').trim().toLowerCase();
    const districtTerm = (selectedDistrict || '').trim().toLowerCase();
    const blockTerm = (selectedBlock || '').trim().toLowerCase();
    const dateTerm = selectedDate || '';

    filtered = filtered.filter((app) => {
      // Text search: NOC id or tank name
      if (term) {
        const hay = `${String(app.id || '').toLowerCase()} ${String(app.tankName || '').toLowerCase()}`;
        if (!hay.includes(term)) return false;
      }
      // Status dropdown
      if (statusTerm && String(app.status || '').toLowerCase() !== statusTerm) return false;
      // District
      if (districtTerm) {
        const loc = String(app.location || '').toLowerCase();
        if (!loc.includes(districtTerm)) return false;
      }
      // Block (L2 filter)
      if (blockTerm) {
        const bn = String(app.blockName || '').toLowerCase();
        if (!bn.includes(blockTerm)) return false;
      }
      // Date (YYYY-MM-DD match against created_at)
      if (dateTerm && app.created_at) {
        const d = new Date(app.created_at).toISOString().slice(0, 10);
        if (d !== dateTerm) return false;
      }
      return true;
    });

    //  if (selectedDate){
        // filtered = filtered.filter(app => 
        // console.log(app,'11111')||
        // console.log(selectedDate,'selectedDate')||
        // // app.id&&String(app.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        // // app.tankName&&app.tankName.toLowerCase().includes(searchTerm.toLowerCase())||
        // (app.created_at && new Date(app.created_at).toISOString().includes(searchTerm))
        // const term = searchTerm.toLowerCase();

        // const filtered = filtered.filter(app =>
          // (app.id && String(app.id).toLowerCase().includes(term)) ||
          // (app.tankName && app.tankName.toLowerCase().includes(term)) ||
          // (app.date && app.date.includes(selectedDate.toLowerCase()))
          // );


        // );
    // }


    {
      const statusOf = (app) => String(app.status || '').toLowerCase();
      const list = totalnocrequestlist || [];
      setPendingNocRequestList(list.filter((app) => statusOf(app) === 'pending'));
      setApprovedNocRequestList(list.filter((app) => ['approved', 'deemed_approved'].includes(statusOf(app))));
      setRejectedNocRequestList(list.filter((app) => statusOf(app) === 'rejected'));
      setReturnedNocRequestList(list.filter((app) => statusOf(app) === 'returned'));
    }
    setFilteredApplications(filtered);
    setAppPage(1);
  }, [searchTerm, selectedStatus, selectedDistrict, selectedBlock, totalnocrequestlist, selectedDate]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDate('');
    setSelectedDistrict('');
    setSelectedStatus('');
  };

  const getStatusColor = (status='pending') => {
    switch(status) {
      case 'approved': return 'bg-emerald-100 text-emerald-800 border-emerald-600';
      case 'pending': return 'bg-orange-100 text-orange-800 border-orange-500';
      case 'rejected': return 'bg-rose-100 text-rose-800 border-rose-600';
      case 'returned': return 'bg-amber-100 text-amber-800 border-amber-500';
      case 'draft': return 'bg-slate-100 text-slate-700 border-slate-400';
      default: return 'bg-slate-100 text-slate-700 border-slate-400';
    }
  };

  const getTimelineStatusColor = (status) => {
    switch(status) {
      case 'submitted': return 'bg-brand-600';
      case 'approve': return 'bg-green-400';
      case 'approved_aee': return 'bg-green-500';
      case 'approved_ee': return 'bg-green-600';
      case 'approved_dc': return 'bg-emerald-600';
      case 'pending_ae_je': return 'bg-orange-400';
      case 'pending_aee': return 'bg-orange-500';
      case 'pending_ee': return 'bg-orange-600';
      case 'pending': return 'bg-orange-700';
      case 'rejected_ae_je': return 'bg-red-400';
      case 'rejected_aee': return 'bg-red-500';
      case 'rejected_ee': return 'bg-red-600';
      case 'rejected': return 'bg-red-700';
      case 'return':return 'bg-orange-400';
      default: return 'bg-gray-500';
    }
  };

  const getTimelineStatusText = (status) => {
    switch(status) {
      case 'submitted': return 'Submitted by Organization';
      case 'approved_ae_je': return 'Approved by AE/JE';
      case 'approved_aee': return 'Approved by AEE';
      case 'approved_ee': return 'Approved by EE';
      case 'approved_dc': return 'Approved by District Collector';
      case 'pending_ae_je': return 'Pending with AE/JE';
      case 'pending_aee': return 'Pending with AEE';
      case 'pending_ee': return 'Pending with EE';
      case 'pending_dc': return 'Pending with District Collector';
      case 'rejected_ae_je': return 'Rejected by AE/JE';
      case 'rejected_aee': return 'Rejected by AEE';

      case 'rejected_ee': return 'Rejected by EE';
      case 'rejected_dc': return 'Rejected by District Collector';
      default: return status;
    }
  };

  const handleStatusClick = async (application_id) => {
    const local = (totalnocrequestlist || []).find(
      (a) => a.application_id === application_id || a.id === application_id
    );
    if (!local) return;
    const numericId = local.application_id;

    setTimelineNOC(numericId);
    setShowTimeline(true);

    // Resolve the active officer's email (if there is one) for the context block.
    // The chain rows already carry email_id/full_name (JOIN'd in /applications/:id/chain),
    // so prefer that. Fall back to /users/:id only if the chain row is missing contact fields.
    let activeOfficerEmail = null;
    const activeChainRow = Array.isArray(local.chain)
      ? local.chain.find((s) => Number(s.user_id) === Number(local.activeUserId))
      : null;
    if (activeChainRow && (activeChainRow.email_id || activeChainRow.full_name || activeChainRow.username)) {
      activeOfficerEmail = activeChainRow.email_id || activeChainRow.full_name || activeChainRow.username;
    } else if (local.activeUserId) {
      try {
        const r = await API.get(`/users/${local.activeUserId}`);
        activeOfficerEmail = r?.data?.data?.email_id || r?.data?.data?.full_name || r?.data?.data?.username || null;
      } catch { /* ignore — we'll show "not mapped" */ }
    }

    setSelectedAppContext({
      id: `NOC${String(numericId).padStart(3, '0')}`,
      tankName: local.tankName,
      district: local.districtName,
      block: local.blockName,
      section: local.sectionName,
      department: local.department,
      status: local.status,
      activeLevel: local.activeLevel,
      activeOfficerEmail,
      hasChain: Array.isArray(local.chain) && local.chain.length > 0,
    });

    // Build a transparent timeline from: NGO submission + each chain step.
    // Don't expose officer names — only the level and its state.
    const submissionStep = {
      date: local.created_at || local.date,
      level: 'NGO',
      stepStatus: 'submitted',
      remarks: 'Application submitted',
    };

    let history = [];
    try {
      const h = await API.get(`/applications/${numericId}/history`, { silent: true });
      history = Array.isArray(h?.data?.data) ? h.data.data : [];
    } catch { /* history endpoint unavailable — fall back to the chain */ }

    const historySteps = history.map((s) => ({
      date: s.action_date,
      level: Number(s.level) === 0 ? 'Applicant' : `Level ${s.level} (L${s.level})`,
      stepStatus: String(s.status || '').toLowerCase(),
      remarks: Number(s.level) === 0 ? 'Application edited and resubmitted' : (s.comments || ''),
    }));
    // Current round's still-pending levels (not in history yet).
    const pendingSteps = (local.chain || [])
      .filter((s) => String(s.status || '').toUpperCase() === 'PENDING' && local.status !== 'rejected' && local.status !== 'returned')
      .sort((a, b) => Number(a.level) - Number(b.level))
      .map((s) => ({ date: null, level: `Level ${s.level} (L${s.level})`, stepStatus: 'pending', remarks: '' }));
    const chainSteps = history.length ? [] : (local.chain || [])
      .slice()
      .sort((a, b) => Number(a.level) - Number(b.level))
      .filter((s) => String(s.status || '').toUpperCase() !== 'PENDING')
      .map((s) => ({
        date: s.action_date,
        level: `Level ${s.level} (L${s.level})`,
        stepStatus: String(s.status || '').toLowerCase(),
        remarks: s.comments || '',
      }));

    setSelectedTimeline([submissionStep, ...historySteps, ...chainSteps, ...pendingSteps]);
  };

  // Compact level strip: one pill per chain step, color-coded by state.
  // Names are intentionally omitted — viewers see "where" not "who".
  const LevelStrip = ({ chain, status }) => {
    if (!Array.isArray(chain) || chain.length === 0) {
      return <span className="text-xs text-gray-400">No chain</span>;
    }
    const sorted = chain.slice().sort((a, b) => Number(a.level) - Number(b.level));
    const appStatus = String(status || '').toLowerCase();
    // Text label alongside icon so L1/L2 states are unambiguous to the reader
    // (client feedback: full workflow timeline with L1/L2 labels).
    const stateLabel = (st) => {
      if (st === 'APPROVED') return 'Approved';
      if (st === 'REJECTED') return 'Rejected';
      if (st === 'RETURNED') return 'Returned';
      if (st === 'PENDING')  return 'Pending';
      return 'Waiting';
    };
    return (
      <div className="flex items-center flex-wrap gap-1">
        {sorted.map((s, i) => {
          const st = String(s.status || '').toUpperCase();
          let cls = 'bg-gray-100 text-gray-500 border-gray-200';
          let mark = '○';
          if (st === 'APPROVED') { cls = 'bg-green-100 text-green-700 border-green-300'; mark = '✓'; }
          else if (st === 'REJECTED') { cls = 'bg-red-100 text-red-700 border-red-300'; mark = '✕'; }
          else if (st === 'RETURNED') { cls = 'bg-amber-100 text-amber-700 border-amber-300'; mark = '↩'; }
          else if (st === 'PENDING' && appStatus !== 'rejected') { cls = 'bg-orange-100 text-orange-700 border-orange-300'; mark = '●'; }
          return (
            <React.Fragment key={s.level}>
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold border ${cls}`}
                title={`L${s.level} — ${stateLabel(st)}`}
              >
                {mark} L{s.level} {stateLabel(st)}
              </span>
              {i < sorted.length - 1 && <span className="text-gray-300 text-xs">→</span>}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const getDashboardTitle = () => {
    switch(userRole) {
      case 'ngo': return 'Organization Dashboard';
      case 'department': return 'Department Dashboard';
      case 'admin': return 'Admin Dashboard';
      default: return 'Dashboard';
    }
  };


  return (
    <div className="bg-slate-100 min-h-screen font-['Inter',sans-serif] pt-16 sm:pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b-4 border-brand-accent">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:py-4 space-y-3 sm:space-y-0">
            {/* <button onClick={onBack} className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mr-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button> */}
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 text-xs sm:text-sm font-medium">Tamil Nadu NOC Portal</span>
              <span className="text-gray-300">•</span>
              <span className="text-brand-700 text-sm sm:text-base font-semibold">{getDashboardTitle()}</span>
            </div>
            
            <div className="flex items-center gap-2">
              {(userRole === 'admin' || userRole === 'dept_admin') && (
                <button
                  onClick={() => (window.location.href = '/admin/department-users')}
                  className="bg-white border border-brand-100 text-brand-800 hover:bg-brand-50 px-4 py-2 font-medium text-sm transition-all"
                  title="Department User Master"
                >
                  Department Users
                </button>
              )}
              {userRole === 'dept_admin' && (
                <button
                  onClick={() => (window.location.href = '/dept-admin-dashboard')}
                  className="bg-white border border-brand-100 text-brand-800 hover:bg-brand-50 px-4 py-2 font-medium text-sm transition-all"
                  title="Department Dashboard"
                >
                  Dashboard
                </button>
              )}
              {userRole === 'admin' && (
                <button
                  onClick={() => (window.location.href = '/state-dashboard')}
                  className="bg-white border border-brand-100 text-brand-800 hover:bg-brand-50 px-4 py-2 font-medium text-sm transition-all"
                  title="State-level monitoring dashboard"
                >
                  State Dashboard
                </button>
              )}
              {userRole === 'ngo' && (
                <button
                  onClick={handleNewNOCRequest}
                  className="bg-brand-700 hover:bg-brand-800 text-white px-4 sm:px-6 py-2 sm:py-2.5 font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 shadow-sm text-sm sm:text-base"
                >
                  <span className="text-lg font-bold">+</span>
                  <span>New NOC Request</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <div className="bg-brand-700 border-b-4 border-brand-900 p-3 sm:p-4 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs opacity-90 font-medium">Total NOCs</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{(totalnocrequestlist || []).filter((a) => String(a.status || '').toLowerCase() !== 'draft').length}</div>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 flex items-center justify-center">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>

          <div className="bg-emerald-600 border-b-4 border-emerald-800 p-3 sm:p-4 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs opacity-90 font-medium">Approved</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{approvednocrequestlist ? approvednocrequestlist.length : '0'}</div>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>

          <div className="bg-amber-500 border-b-4 border-amber-700 p-3 sm:p-4 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs opacity-90 font-medium">Pending</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{pendingnocrequestlist ? pendingnocrequestlist.length : '0'}</div>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 flex items-center justify-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>

          <div className="bg-rose-600 border-b-4 border-rose-800 p-3 sm:p-4 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                  <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs opacity-90 font-medium">Rejected</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{rejectednocrequestlist ? rejectednocrequestlist.length : '0'}</div>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 flex items-center justify-center">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>

          <div className="bg-orange-600 border-b-4 border-orange-800 p-3 sm:p-4 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                  <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs opacity-90 font-medium">Returned</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold">{returnednocrequestlist ? returnednocrequestlist.length : '0'}</div>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 flex items-center justify-center">
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-brand-700" />
              <span className="font-semibold text-gray-800 text-sm sm:text-base">Quick Filters</span>
            </div>
            <button onClick={clearFilters} className="text-brand-700 text-sm font-medium hover:text-brand-800 self-start sm:self-auto">Clear All</button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by NOC ID or Tank..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
              />
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            </div>
            <div className="relative">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
              />
              <Calendar className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            </div>
            {/* L2 officers filter by Block; everyone else by District. */}
            {Number(authUser?.level) === 2 ? (
              <input
                type="text"
                placeholder="Block"
                value={selectedBlock}
                onChange={(e) => setSelectedBlock(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
              />
            ) : (
              <input
                type="text"
                placeholder="District"
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
              />
            )}
            <select 
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="returned">Returned</option>
            </select>
          </div>
        </div>

        {/* Applications Table */}
        {(() => null)()}
        {/* Pagination math */}
        {(() => {})()}
        <div className="bg-white shadow-sm overflow-hidden border border-gray-200">
          <div className="px-4 sm:px-6 py-3 bg-brand-800">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
              <h3 className="text-lg font-semibold text-white">My NOC Applications</h3>
              <span className="text-sm text-brand-100">{filteredApplications ? filteredApplications.length : 0} applications found</span>
            </div>
          </div>

          {(() => null)()}
          {/* Mobile Card View */}
          <div className="block sm:hidden">
            { filteredApplications ?filteredApplications
              .slice((appPage - 1) * APP_PAGE_SIZE, appPage * APP_PAGE_SIZE)
              .map((app, index) => (


              <div key={app.id} className="border-b border-slate-200 p-4 odd:bg-white even:bg-brand-50 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <button
                      type="button"
                      onClick={() => handleWorkflowView(app.id)}
                      className="text-sm font-semibold text-brand-700 mb-1 hover:underline hover:text-brand-900"
                      title="Open full application"
                    >
                      {app.id}
                    </button>
                    <div className="text-base font-medium text-gray-900">{app.tankName}</div>
                    <div className="text-sm text-gray-600">{app.location}</div>
                    <div className="text-sm text-gray-500 mt-1">{app.department}</div>
                  </div>





                 <button
                  onClick={() => handleStatusClick(app.application_id)}
                  className={`inline-flex px-3 py-1 text-xs font-semibold border-0 border-l-4 ${getStatusColor(app.status)} cursor-pointer`}
                >
                  {app.status ? app.status.charAt(0).toUpperCase() + app.status.slice(1) : 'Pending'}
                  {app.status === 'pending' && app.activeLevel ? ` • L${app.activeLevel}` : ''}
                </button>
                </div>
                <div className="mb-3">
                  <LevelStrip chain={app.chain} status={app.status} />
                </div>
                
                <div className="mb-3">
                  <div className="text-xs text-gray-600 font-medium mb-2">Activities:</div>
                  <div className="flex flex-wrap gap-1">
                    {Array.isArray(app.activities) ? app.activities.map((activity, idx) => (
                      <span key={idx} className="inline-flex px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 border border-brand-100">
                        {activity}
                      </span>
                    )) : null}
                  </div>
                </div>
                
                {userRole === 'ngo' && app.status === 'draft' && (
                  <div className="flex justify-end gap-2 flex-wrap">
                    <button
                      onClick={() => openEdit(app)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 border border-amber-200 hover:bg-amber-200"
                      title="Edit this draft"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleSubmitDraft(app.application_id)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-brand-800 bg-brand-100 border border-brand-100 hover:bg-brand-100"
                    >
                      Submit Draft
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(app.application_id)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-200 hover:bg-red-200"
                      title="Delete this draft"
                    >
                      Delete
                    </button>
                  </div>
                )}
                {userRole !== 'ngo' && app.returnedToL1Reason && (
                  <div className="mb-3 border-l-4 p-3 text-sm bg-amber-50 border-amber-500 text-amber-900">
                    <div className="flex items-center gap-1.5 font-semibold mb-1">
                      <AlertTriangle className="w-4 h-4" />
                      Returned to L1 by L{app.returnedToL1Level}
                    </div>
                    <p className="whitespace-pre-line">{app.returnedToL1Reason}</p>
                  </div>
                )}
                {(app.status === 'returned' || app.status === 'rejected') && (
                  <div className={`mb-3 border-l-4 p-3 text-sm ${app.status === 'rejected' ? 'bg-red-50 border-red-500 text-red-900' : 'bg-amber-50 border-amber-500 text-amber-900'}`}>
                    <div className="flex items-center gap-1.5 font-semibold mb-1">
                      <AlertTriangle className="w-4 h-4" />
                      {app.status === 'rejected' ? 'Reason for rejection' : 'Reason for return'}
                      {app.latestReasonLevel ? ` (L${app.latestReasonLevel})` : ''}
                    </div>
                    <p className="whitespace-pre-line">{app.latestReason || 'No remarks were recorded by the officer.'}</p>
                  </div>
                )}
                {userRole === 'ngo' && app.status === 'returned' && (
                  <div className="flex justify-end gap-2 flex-wrap">
                    <button
                      onClick={() => openEdit(app)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 border border-amber-200 hover:bg-amber-200"
                      title="Edit & Resubmit — the returned application will re-enter the workflow at L1."
                    >
                      Edit & Resubmit
                    </button>
                  </div>
                )}

                {userRole === 'ngo' && app.status === 'approved' && (
                  <div className="flex justify-end gap-2 flex-wrap">
                    <button
                      onClick={() => handleWorkflowView(app.id)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-brand-800 bg-brand-100 border border-brand-100 hover:bg-brand-100 transition-all duration-200"
                      title="Open application (upload restoration photos, view chain)"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Open
                    </button>
                    <button
                      onClick={() => handleDownload(app.id)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-200 hover:bg-green-200 transition-all duration-200"
                      title="Download NOC Certificate"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Certificate
                    </button>
                  </div>
                )}

                {userRole === 'department' && (
                  <div className="flex justify-end space-x-2 flex-wrap gap-y-2">
                    <button
                      onClick={() => handleWorkflowView(app.id)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-brand-800 bg-brand-100 border border-brand-100 hover:bg-brand-100 transition-all duration-200"
                      title="View Workflow"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </button>
                    {app.isMyTurn && (
                      <>
                        <button
                          onClick={() => handleApprove(app.application_id)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-200 hover:bg-green-200"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(app.application_id)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-200 hover:bg-red-200"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </button>
                      </>
                    )}
                    {app.status === 'approved' && (
                      <button
                        onClick={() => handleDownload(app.id)}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-200 hover:bg-green-200 transition-all duration-200"
                        title="Download NOC Certificate"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </button>
                    )}
                  </div>
                )}
              </div>
            )) : ''}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-brand-600">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">NOC ID</th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Tank Details</th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Activities</th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredApplications?filteredApplications
                  .slice((appPage - 1) * APP_PAGE_SIZE, appPage * APP_PAGE_SIZE)
                  .map((app, index) => (
                  <tr key={app.id} className={`hover:bg-brand-100 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-brand-50'}`}>
                    <td className="px-4 lg:px-6 py-4">
                      <button
                        type="button"
                        onClick={() => handleWorkflowView(app.id)}
                        className="text-sm font-semibold text-brand-700 hover:underline hover:text-brand-900"
                        title="Open full application"
                      >
                        {app.id}
                      </button>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{app.tankName}</div>
                        <div className="text-xs text-gray-600">{app.location}</div>
                        <div className="text-xs text-gray-500 mt-1">{app.department}</div>
                        {/* Show activities on tablet */}
                        <div className="lg:hidden mt-2">
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(app.activities) ? app.activities.slice(0, 2).map((activity, idx) => (
                              <span key={idx} className="inline-flex px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 border border-brand-100">
                                {activity}
                              </span>
                            )) : null}
                            {Array.isArray(app.activities) && app.activities.length > 2 && (
                              <span className="text-xs text-gray-500">+{app.activities.length - 2} more</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {Array.isArray(app.activities) ? app.activities.map((activity, idx) => (
                          <span key={idx} className="inline-flex px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 border border-brand-100">
                            {activity}
                          </span>
                        )) : <span key={''} className="inline-flex px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 border border-brand-100">
                            {app.activities}
                          </span>}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleStatusClick(app.application_id)}
                          className={`inline-flex px-3 py-1 text-xs font-semibold border-0 border-l-4 ${getStatusColor(app.status)} hover:shadow-md transition-all duration-200 cursor-pointer w-fit`}
                        >
                          {app.status ? app.status.charAt(0).toUpperCase() + app.status.slice(1) : 'Pending'}
                        </button>
                        <LevelStrip chain={app.chain} status={app.status} />
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      {userRole === 'ngo' && app.status === 'draft' && (
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => openEdit(app)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 hover:bg-amber-200"
                            title="Edit this draft"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleSubmitDraft(app.application_id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-brand-800 bg-brand-100 border border-brand-100 hover:bg-brand-100"
                            title="Submit this draft for approval"
                          >
                            Submit
                          </button>
                          <button
                            onClick={() => handleDeleteDraft(app.application_id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-200 hover:bg-red-200"
                            title="Delete this draft"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                      {userRole !== 'ngo' && app.returnedToL1Reason && (
                        <p className="w-full text-xs mb-1 text-amber-700" title={app.returnedToL1Reason}>
                          <b>Returned to L1 by L{app.returnedToL1Level}:</b> {app.returnedToL1Reason}
                        </p>
                      )}
                      {(app.status === 'returned' || app.status === 'rejected') && (
                        <p className={`w-full text-xs mb-1 ${app.status === 'rejected' ? 'text-red-700' : 'text-amber-700'}`} title={app.latestReason}>
                          <b>{app.status === 'rejected' ? 'Rejected' : 'Returned'}{app.latestReasonLevel ? ` (L${app.latestReasonLevel})` : ''}:</b>{' '}
                          {app.latestReason || 'No remarks recorded'}
                        </p>
                      )}
                      {userRole === 'ngo' && app.status === 'returned' && (
                        <button
                          onClick={() => openEdit(app)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 hover:bg-amber-200"
                          title="Edit & Resubmit"
                        >
                          Edit & Resubmit
                        </button>
                      )}
                      {userRole === 'ngo' && app.status === 'approved' && (
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => handleWorkflowView(app.id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-brand-800 bg-brand-100 border border-brand-100 hover:bg-brand-100"
                            title="Open application (upload restoration photos, view chain)"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            <span className="hidden md:inline">Open</span>
                          </button>
                          <button
                            onClick={() => handleDownload(app.id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-200 hover:bg-green-200"
                            title="Download NOC Certificate"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            <span className="hidden md:inline">Download</span>
                          </button>
                        </div>
                      )}
                      {userRole === 'department' && (
                        <div className="flex space-x-1 flex-wrap gap-y-1">
                          <button
                            onClick={() => handleWorkflowView(app.id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-brand-800 bg-brand-100 border border-brand-100 hover:bg-brand-100 transition-all duration-200"
                            title="View Workflow"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            <span className="hidden lg:inline">View</span>
                          </button>
                          {app.isMyTurn && (
                            <>
                              <button
                                onClick={() => handleApprove(app.application_id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-200 hover:bg-green-200"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                <span className="hidden lg:inline">Approve</span>
                              </button>
                              <button
                                onClick={() => handleReject(app.application_id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-200 hover:bg-red-200"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                <span className="hidden lg:inline">Reject</span>
                              </button>
                            </>
                          )}
                          {app.status === 'approved' && (
                            <button
                              onClick={() => handleDownload(app.id)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-200 hover:bg-green-200 transition-all duration-200"
                              title="Download NOC Certificate"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              <span className="hidden lg:inline">Download</span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )): <tr >
                    <td colSpan={5} className="px-4 lg:px-6 py-4">
                      <div className="text-sm font-semibold text-brand-700 text-center">No Record Found</div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          {filteredApplications && filteredApplications.length > APP_PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 gap-2">
              <span className="text-xs text-gray-600">
                Showing {(appPage - 1) * APP_PAGE_SIZE + 1}
                –{Math.min(appPage * APP_PAGE_SIZE, filteredApplications.length)} of {filteredApplications.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setAppPage((p) => Math.max(1, p - 1))}
                  disabled={appPage === 1}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  ‹ Prev
                </button>
                {(() => {
                  const total = Math.ceil(filteredApplications.length / APP_PAGE_SIZE);
                  const max = Math.min(total, 5);
                  let start = Math.max(1, Math.min(appPage - 2, total - max + 1));
                  return Array.from({ length: max }, (_, i) => start + i).map((p) => (
                    <button
                      key={p}
                      onClick={() => setAppPage(p)}
                      className={`px-3 py-1.5 text-xs font-medium ${
                        p === appPage
                          ? 'bg-brand-700 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => setAppPage((p) => Math.min(Math.ceil(filteredApplications.length / APP_PAGE_SIZE), p + 1))}
                  disabled={appPage >= Math.ceil(filteredApplications.length / APP_PAGE_SIZE)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Modal */}
     {showTimeline && selectedTimeline && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-4 sm:px-6 py-4 bg-brand-800 border-b-4 border-brand-accent">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">Application Timeline</h3>
                  <p className="text-brand-100 text-sm">NOC ID: NOC{String(TimelineNOC).padStart(3, '0')}</p>
                </div>
                <button
                  onClick={() => setShowTimeline(false)}
                  className="text-white hover:text-gray-200 transition-colors p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[70vh]">
              {/* Current location of the application — where it's sitting right now */}
              {selectedAppContext && (
                <div className={`mb-4 border p-4 ${
                  selectedAppContext.status === 'pending'
                    ? 'bg-orange-50 border-orange-200'
                    : selectedAppContext.status === 'returned'
                      ? 'bg-amber-50 border-amber-200'
                      : selectedAppContext.status === 'rejected'
                        ? 'bg-red-50 border-red-200'
                        : selectedAppContext.status === 'approved'
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                    {selectedAppContext.status === 'pending' ? 'Currently with' :
                     selectedAppContext.status === 'returned' ? 'Returned to applicant' :
                     selectedAppContext.status === 'rejected' ? 'Rejected at' :
                     selectedAppContext.status === 'approved' ? 'Final outcome' :
                     'Application location'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <div><span className="text-gray-500">Tank:</span> <b className="text-gray-900">{selectedAppContext.tankName || '—'}</b></div>
                    <div><span className="text-gray-500">District:</span> <b className="text-gray-900">{selectedAppContext.district || '—'}</b></div>
                    {selectedAppContext.block && (
                      <div><span className="text-gray-500">Block:</span> <b className="text-gray-900">{selectedAppContext.block}</b></div>
                    )}
                    {selectedAppContext.section && (
                      <div><span className="text-gray-500">Section:</span> <b className="text-gray-900">{selectedAppContext.section}</b></div>
                    )}
                    <div><span className="text-gray-500">Department:</span> <b className="text-gray-900">{selectedAppContext.department || '—'}</b></div>
                    {selectedAppContext.status === 'pending' && selectedAppContext.activeLevel && (
                      <div className="sm:col-span-2 mt-1 pt-2 border-t border-gray-200">
                        <span className="text-gray-500">Pending with officer (Level {selectedAppContext.activeLevel}):</span>
                        <br />
                        {selectedAppContext.activeOfficerEmail ? (
                          <a href={`mailto:${selectedAppContext.activeOfficerEmail}`}
                             className="text-brand-800 font-semibold break-all hover:underline">
                            {selectedAppContext.activeOfficerEmail}
                          </a>
                        ) : (
                          <span className="text-amber-700 font-semibold">Officer details not available</span>
                        )}
                      </div>
                    )}
                    {!selectedAppContext.hasChain && (
                      <div className="sm:col-span-2 mt-1 pt-2 border-t border-gray-200 text-amber-700">
                        <b>Approval chain not built yet</b> — no officer is mapped for this department + district
                        {selectedAppContext.section ? ` + ${selectedAppContext.section} section` : ''}.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {selectedTimeline && selectedTimeline.length > 0 ? selectedTimeline.map((item, index) => {
                  const st = String(item.stepStatus || '').toLowerCase();
                  let dot = 'bg-gray-300';
                  let label = 'Awaiting';
                  if (st === 'submitted') { dot = 'bg-brand-600'; label = 'Submitted'; }
                  else if (st === 'approved') { dot = 'bg-green-500'; label = 'Approved'; }
                  else if (st === 'rejected') { dot = 'bg-red-500'; label = 'Rejected'; }
                  else if (st === 'returned') { dot = 'bg-amber-500'; label = 'Returned to applicant'; }
                  else if (st === 'returned_to_l1') { dot = 'bg-amber-500'; label = 'Returned to L1'; }
                  else if (st === 'pending') { dot = 'bg-orange-400'; label = 'Pending'; }
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className={`w-4 h-4 rounded-full ${dot} mt-1`}></div>
                        {index < selectedTimeline.length - 1 && (
                          <div className="w-0.5 h-16 sm:h-20 bg-gray-300 ml-1.5 mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
                          <span className="text-sm font-semibold text-gray-900">
                            {item.level} — {label}
                          </span>
                          {item.date && (
                            <span className="text-xs text-gray-500 self-start">
                              {new Date(item.date).toLocaleDateString('en-IN')}
                            </span>
                          )}
                        </div>
                        {item.remarks && (
                          <div className="bg-gray-50 p-3">
                            <p className="text-xs text-gray-600 font-medium mb-1">Remarks:</p>
                            <p className="text-sm text-gray-800">{item.remarks}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-gray-500 text-center py-8">No timeline available.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )} 
    </div>
  );
}